// Blog content. Plain data (Node-safe) so routesMeta.js can build Article
// JSON-LD + sitemap entries and the blog pages can render the same content.
//
// Each post: { slug, title, description, date, readMinutes, excerpt, tags,
//   content: [ block ] } where block is:
//   { t: 'p', text } | { t: 'h2', text } | { t: 'ul', items: [] }
//
// Adding a post here automatically creates its route metadata (see routesMeta)
// and a prerendered /blog/<slug>/index.html at build time.

export const posts = [
  {
    slug: 'how-to-choose-limo-dispatch-software',
    title: 'How to Choose Limo Dispatch Software (2026 Buyer’s Guide)',
    description:
      'A practical, no-nonsense guide to choosing limo dispatch software in 2026 — the features that matter, the pricing traps to avoid, and the questions to ask every vendor.',
    date: '2026-08-15',
    readMinutes: 7,
    tags: ['Buyer’s Guide', 'Dispatch'],
    excerpt:
      'The right dispatch platform pays for itself in saved hours and fewer missed trips. Here is how to evaluate your options without getting lost in feature checklists.',
    content: [
      { t: 'p', text: 'Choosing dispatch software is one of the highest-leverage decisions a ground transportation operator makes. The right system saves hours every day, reduces missed and duplicated trips, and gives customers a better experience. The wrong one adds friction and hidden costs. This guide walks through how to evaluate your options in 2026.' },
      { t: 'h2', text: '1. Start with your real workflow, not a feature list' },
      { t: 'p', text: 'Vendors love feature checklists, but the question that matters is whether the software fits how your team actually works. Map your day — reservation intake, assignment, driver communication, billing — and evaluate each platform against those steps.' },
      { t: 'h2', text: '2. Decide how much you want automated' },
      { t: 'p', text: 'Auto-dispatch can assign trips by vehicle type, capacity, pick-up time, and special needs without a dispatcher touching them. But you still want manual override for VIPs and exceptions. Look for a platform that does both well rather than forcing one model.' },
      { t: 'h2', text: '3. Insist on branded driver and passenger apps' },
      { t: 'p', text: 'Your chauffeurs need trip details, GPS, status updates, and in-vehicle payment. Your customers increasingly expect to book from their phone. A white-label passenger app keeps that relationship — and the margin — with you instead of a marketplace.' },
      { t: 'h2', text: '4. Scrutinize the pricing model' },
      { t: 'ul', items: [
        'Per-trip commissions get expensive fast as you grow — model your annual cost at realistic volume.',
        'Watch for features locked behind higher tiers you will inevitably need.',
        'Ask about setup, data migration, and training fees up front.',
        'Prefer transparent licensing that stays predictable as volume grows.',
      ] },
      { t: 'h2', text: '5. Test support before you buy' },
      { t: 'p', text: 'When a trip is on the line, you need a human. Ask how support is delivered, what hours it covers, and whether you get a dedicated contact. A vendor with deep industry experience will understand your problem before you finish describing it.' },
      { t: 'h2', text: 'Questions to ask every vendor' },
      { t: 'ul', items: [
        'How does auto-dispatch decide assignments, and can I override it?',
        'Are the driver and passenger apps fully branded to my company?',
        'Can customers book directly from my website into dispatch?',
        'What is the all-in annual cost at my trip volume?',
        'What does onboarding, migration, and training include?',
        'What are your support hours and response times?',
      ] },
      { t: 'p', text: 'Limosys checks each of these boxes — manual and auto-dispatch, branded apps, website booking, transparent licensing, and 24/7 support from a team that has served ground transportation for over 30 years. If you want to see it against your workflow, request a demo.' },
    ],
  },
  {
    slug: 'limousine-dispatch-software-guide',
    title: 'Limousine Dispatch Software: The Complete Guide',
    description:
      'What limousine dispatch software does, the core modules to expect, and how it connects reservations, dispatch, apps, and billing into one operation.',
    date: '2026-08-08',
    readMinutes: 6,
    tags: ['Dispatch', 'Fundamentals'],
    excerpt:
      'A plain-English overview of what limousine dispatch software is, what it should include, and how the pieces fit together.',
    content: [
      { t: 'p', text: 'Limousine dispatch software is the operational backbone of a chauffeured transportation business. It connects the moment a customer books to the moment the trip is completed and paid for — and everything in between. Here is what it includes and how the pieces fit.' },
      { t: 'h2', text: 'Reservations and booking' },
      { t: 'p', text: 'Reservations arrive from your website, a branded passenger app, phone calls, or corporate accounts. Good software captures them all in one place with instant quotes, flight tracking, and stored payment methods.' },
      { t: 'h2', text: 'Dispatch and routing' },
      { t: 'p', text: 'The dispatch board is where trips are assigned to vehicles and chauffeurs. Automated dispatch matches trips to the right vehicle by type, capacity, and timing; manual dispatch gives your team full control when they need it.' },
      { t: 'h2', text: 'Driver and passenger apps' },
      { t: 'ul', items: [
        'Driver app: trip details, GPS navigation, status updates, in-vehicle payments, e-signatures.',
        'Passenger app: booking, live vehicle tracking, rate previews, trip history — under your brand.',
      ] },
      { t: 'h2', text: 'Back office: billing, payroll, reporting' },
      { t: 'p', text: 'Once trips run, the platform handles invoicing, credit card processing, payroll disbursements, expenses, and reporting so your financials tie directly to the work dispatched.' },
      { t: 'h2', text: 'Why an integrated platform matters' },
      { t: 'p', text: 'Stitching together separate booking, dispatch, and payment tools creates double entry and errors. An integrated platform like Limosys keeps one live source of truth across the whole operation — which is why operators have relied on it for over 30 years.' },
    ],
  },
  {
    slug: 'auto-dispatch-vs-manual-dispatch',
    title: 'Auto-Dispatch vs Manual Dispatch: Which Is Right for You?',
    description:
      'Automated dispatch saves time; manual dispatch gives control. Here is how to decide — and why most successful operators run a hybrid of both.',
    date: '2026-07-28',
    readMinutes: 5,
    tags: ['Dispatch', 'Operations'],
    excerpt:
      'Automation or human control? The best operators do not choose — they combine them. Here is how to think about the mix.',
    content: [
      { t: 'p', text: 'One of the most common questions operators ask is whether to run automated or manual dispatch. The honest answer: the best operations use both, and the software should make switching effortless.' },
      { t: 'h2', text: 'What auto-dispatch does well' },
      { t: 'ul', items: [
        'Assigns routine trips instantly by vehicle type, capacity, timing, and special needs.',
        'Reduces dispatcher workload and human error at scale.',
        'Keeps the board moving during busy periods without bottlenecks.',
      ] },
      { t: 'h2', text: 'When manual control wins' },
      { t: 'ul', items: [
        'VIP and high-value clients that need a specific chauffeur or vehicle.',
        'Complex multi-stop or last-minute changes.',
        'Exceptions where judgment beats a rule.',
      ] },
      { t: 'h2', text: 'The hybrid model' },
      { t: 'p', text: 'Let automation handle the routine so your dispatchers can focus on the trips that need a human. With Limosys, dispatchers can override any auto-assignment at any time, so you get the efficiency of automation and the control of manual dispatch in the same board.' },
    ],
  },
  {
    slug: 'black-car-booking-software-features',
    title: 'What Black Car Operators Should Look for in Booking Software',
    description:
      'Black car and livery operators have specific needs. Here are the booking and dispatch features that matter most for a premium, corporate-grade service.',
    date: '2026-07-18',
    readMinutes: 5,
    tags: ['Black Car', 'Booking'],
    excerpt:
      'Corporate and black car work rewards reliability and polish. These are the software features that protect both.',
    content: [
      { t: 'p', text: 'Black car and livery operators serve demanding clients — corporate travelers, executives, and repeat VIPs — where reliability and professionalism are the product. Your booking software should reinforce that, not undermine it.' },
      { t: 'h2', text: 'Features that matter most' },
      { t: 'ul', items: [
        'A branded booking experience on your website and app — never a third-party marketplace.',
        'Flight tracking and airport meet-and-greet support.',
        'Corporate accounts, profiles, and repeat-booking in a few taps.',
        'Reliable auto-dispatch with manual override for VIPs.',
        'Real-time GPS so you can give accurate ETAs.',
        'Stored payment methods and clean, professional invoicing.',
      ] },
      { t: 'h2', text: 'Reliability is a feature' },
      { t: 'p', text: 'Automated confirmations reduce no-shows, real-time tracking keeps dispatch informed, and integrated billing prevents disputes. Together they protect the reputation that black car operators live on.' },
      { t: 'p', text: 'Limosys was built for exactly this kind of operation. See how it handles your black car and corporate work — request a demo.' },
    ],
  },
  {
    slug: 'reduce-no-shows-automated-notifications',
    title: 'How to Reduce No-Shows with Automated Notifications',
    description:
      'No-shows and last-minute cancellations quietly drain revenue. Automated voice and text confirmations are one of the simplest, highest-ROI fixes.',
    date: '2026-07-05',
    readMinutes: 4,
    tags: ['Operations', 'Automation'],
    excerpt:
      'No-shows waste dispatches and payroll. Automated confirmations are a small change with an outsized return.',
    content: [
      { t: 'p', text: 'Every no-show is a vehicle dispatched, a chauffeur paid, and revenue lost. Last-minute cancellations are almost as costly. The good news: automated notifications are one of the simplest ways to cut both.' },
      { t: 'h2', text: 'Why notifications work' },
      { t: 'p', text: 'Automated voice and text confirmations remind passengers of their trip, confirm the details, and give them an easy way to adjust before a vehicle is committed. That small nudge dramatically reduces forgotten and abandoned bookings.' },
      { t: 'h2', text: 'What to automate' },
      { t: 'ul', items: [
        'Booking confirmation immediately after reservation.',
        'A reminder ahead of the pick-up window.',
        'Driver-en-route and vehicle-arrived alerts.',
        'Easy reschedule or cancel links that update dispatch automatically.',
      ] },
      { t: 'h2', text: 'The bottom line' },
      { t: 'p', text: 'Fewer no-shows means fewer wasted dispatches and lower payroll cost — money straight back to your margin. Limosys includes a customizable automated notification system out of the box.' },
    ],
  },
];

export const postBySlug = new Map(posts.map((p) => [p.slug, p]));
